import { useId } from 'react';
import { t } from '../../lib/i18n';

export interface DateRange {
  /** yyyy-mm-dd or empty. */
  from: string;
  to: string;
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Quick ranges relative to `today` (a local date). */
export function presetRanges(today: Date = new Date()): { key: string; labelKey: string; range: DateRange }[] {
  const ago = (n: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return iso(d);
  };
  return [
    { key: 'today', labelKey: 'p2w.range.today', range: { from: iso(today), to: iso(today) } },
    { key: 'last7', labelKey: 'p2w.range.last7', range: { from: ago(6), to: iso(today) } },
    { key: 'last30', labelKey: 'p2w.range.last30', range: { from: ago(29), to: iso(today) } },
    { key: 'month', labelKey: 'p2w.range.thisMonth', range: { from: iso(new Date(today.getFullYear(), today.getMonth(), 1)), to: iso(today) } },
  ];
}

export function isRangeValid(r: DateRange): boolean {
  return !r.from || !r.to || r.from <= r.to;
}

const INPUT =
  'block min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 hover:border-slate-400 disabled:bg-slate-100 aria-[invalid=true]:border-red-600 sm:min-h-9';

/** From and To dates (yyyy-mm-dd) with optional quick ranges. Either side may be empty for an open range. */
export function DateRangePicker({
  label,
  value,
  onChange,
  presets = true,
  today,
  disabled,
  min,
  max,
}: {
  label: string;
  value: DateRange;
  onChange: (r: DateRange) => void;
  presets?: boolean;
  /** For tests and for "today in the site's time zone". */
  today?: Date;
  disabled?: boolean;
  min?: string;
  max?: string;
}) {
  const base = useId();
  const valid = isRangeValid(value);
  const invalidProps = { 'aria-invalid': valid ? undefined : (true as const), 'aria-describedby': valid ? undefined : `${base}-err` };
  return (
    <fieldset className="min-w-0" disabled={disabled}>
      <legend className="mb-1 text-sm font-medium text-slate-800">{label}</legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${base}-from`} className="mb-1 block text-xs text-slate-700">
            {t('p2w.range.from')}
          </label>
          <input id={`${base}-from`} type="date" value={value.from} min={min} max={value.to || max} {...invalidProps} onChange={(e) => onChange({ ...value, from: e.target.value })} className={INPUT} />
        </div>
        <div>
          <label htmlFor={`${base}-to`} className="mb-1 block text-xs text-slate-700">
            {t('p2w.range.to')}
          </label>
          <input id={`${base}-to`} type="date" value={value.to} min={value.from || min} max={max} {...invalidProps} onChange={(e) => onChange({ ...value, to: e.target.value })} className={INPUT} />
        </div>
      </div>
      {presets ? (
        <div role="group" aria-label={t('p2w.range.presets')} className="mt-2 flex flex-wrap gap-2">
          {presetRanges(today).map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => onChange(p.range)}
              className="min-h-10 rounded-full border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 sm:min-h-8"
            >
              {t(p.labelKey)}
            </button>
          ))}
        </div>
      ) : null}
      {valid ? null : (
        <p id={`${base}-err`} role="alert" className="mt-1 text-sm text-red-700">
          {t('p2w.range.invalid')}
        </p>
      )}
    </fieldset>
  );
}
